import { useEffect, useRef, type ReactNode } from "react";
import s from "./Drawer.module.css";
import { t } from "../../i18n";

/**
 * A panel that slides in from the right with the detail behind whatever was
 * clicked. A modal <dialog>, so Escape closes it, focus stays inside, and the
 * page behind is inert while it is open.
 */
export function Drawer({
  title,
  sub,
  onClose,
  children,
}: {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup — see CreateWorkOrderDialog.
  }, []);
  return (
    <dialog
      ref={ref}
      className={s.drawer}
      onClose={onClose}
      onClick={(e) => {
        // A click on the backdrop (the dialog itself, outside the panel) closes it.
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={s.panel}>
        <header className={s.head}>
          <div>
            <h2 className={s.title}>{title}</h2>
            {sub && <div className={s.sub}>{sub}</div>}
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label={t("Close")}>
            ×
          </button>
        </header>
        <div className={s.body}>{children}</div>
      </div>
    </dialog>
  );
}

/** A labelled fact inside a drawer: what it is, and where it came from. */
export function Fact({ label, children, source }: { label: string; children: ReactNode; source?: ReactNode }) {
  return (
    <div className={s.fact}>
      <dt>{label}</dt>
      <dd>
        {children}
        {source && (
          <div className={s.source}>
            {t("Source:")} {source}
          </div>
        )}
      </dd>
    </div>
  );
}

export const drawerStyles = s;
